import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraCheckboxField, SpecraInput, SpecraTabBar, SpecraTabPanel, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EmptyState } from '../../../components/layout';
import { rateBookHref } from '../../../components/SharedRecord';
import { useDebounce } from '../../../hooks/useDebounce';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { deliveryClient } from '../../../lib/connect/clients';
import { cn } from '../../../lib/utils';
import { mappingVariant } from '../../Registries/badgeVariants';
import { RegistryListNote } from '../../Registries/list/RegistryListNote';
import { OpenFullPage, useRowOpen } from '../../Registries/list/RowOpen';
import { useShowMore } from '../../Registries/list/useShowMore';
import { useRegistryRecordsEnabled } from '../../Registries/registryFlags';
import { registryHref } from '../../Registries/registryHref';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { useOwnCompanyProfile } from '../Programs/referenceDataHooks';
import { CandidateDecisionPanel } from './CandidateDecisionPanel';
import { EntryProvenanceSection } from './EntryProvenanceSection';
import { BURDEN_LABELS, companyProfileHooks, contractVehicleHooks, laborCategoryHooks, RATE_TYPE_LABELS, useAcceptLaborCategoryCandidate, useCreateRateObservation, useLaborCategoryAnchors, useLaborCategoryCandidates, useLaborCategoryHistory, useLaborCategoryMappings, useProposeLaborCategoryMapping, useRateObservations, useRejectLaborCategoryCandidate, useRetireLaborCategoryEntry } from './hooks';
import { LcatCompareView } from './LcatCompareView';
import { MappingReviewPanel } from './MappingReviewPanel';
import { MAPPING_STATE_LABELS, MAPPING_TYPE_LABELS, mappingStateFor, mappingStatement, mappingViewFrom } from './mappingDirection';
import { RateObservationHistory } from './RateObservationHistory';
import { useRegistryRecord } from './registryParams';
import { rosettaCompareHref } from './rosettaComparison';
// RATE_TYPE_LABELS (hooks.ts) predates FLOOR and AWARDED — the repository's
// closed set (delivery_rate_observations_repository.go) accepts both, so
// extend locally rather than touching the shared map. PAY is never added
// here: it must stay unofferable in the type select and unrenderable in any
// rate row (C4-13).
const ALL_RATE_TYPE_LABELS = {
    ...RATE_TYPE_LABELS,
    FLOOR: 'Floor (minimum)',
    AWARDED: 'Awarded'
};
// The repository's accepted rate_unit set — offered so a curator can state
// the unit rather than let it be silently assumed (C4-02).
const UNIT_NOT_STATED = 'NOT_STATED';
// Mirrors the repository's perHourTypes: FIXED is a lump sum and is refused
// on them, so it is not offered there.
const PER_HOUR_TYPES = new Set([
    'CEILING',
    'BILLED',
    'INFERRED',
    'FLOOR',
    'AWARDED'
]);
const RATE_UNIT_LABELS = {
    [UNIT_NOT_STATED]: 'Not stated',
    HOURLY: 'Hourly',
    DAILY: 'Daily',
    MONTHLY: 'Monthly',
    ANNUAL: 'Annual',
    FIXED: 'Fixed',
    ITERATION: 'Per iteration'
};
// Evidence keys the candidate inspect panel calls out explicitly; everything
// else in evidence_json still renders, just generically, so nothing is hidden.
const EVIDENCE_CALLOUT_KEYS = [
    'source_filename',
    'source_document_id',
    'page_number',
    'section_number',
    'exact_text',
    'confidence'
];
// Compact reviewer-facing summary of a mapping's evidence_json — importers
// and the zou feed attach real evidence; hand-proposed edges carry "{}".
const evidenceSummary = (json)=>{
    try {
        const obj = JSON.parse(json);
        if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) return '—';
        const parts = Object.entries(obj).map(([k, v])=>`${k}: ${String(v)}`);
        return parts.length ? parts.join(' · ') : '—';
    } catch  {
        return '—';
    }
};
// A dollar sign asserts dollars — stamping it on a non-USD amount reads as a
// wrong currency, not a formatting quirk. USD keeps the bare "$" form pinned
// by existing tests; anything else states its ISO code instead of borrowing
// the sign that isn't its own.
const formatRateAmount = (currency, amount)=>currency && currency !== 'USD' ? `${currency} ${amount.toFixed(2)}` : `$${amount.toFixed(2)}`;
// An absent fact is not evidence — null/undefined/''/[]/{} all mean "not
// carried into this record" and should disappear rather than read as noise.
const isEmptyEvidenceValue = (v)=>v === null || v === undefined || v === '' || Array.isArray(v) && v.length === 0 || typeof v === 'object' && Object.keys(v).length === 0;
// The generic evidence list (anything not in EVIDENCE_CALLOUT_KEYS) renders
// whatever the feeder attached, so it has to cope with the zou feeder's
// nested shapes: education_detail/experience_detail are one level of object,
// not scalars. Flatten that one level — "education_detail.degree" — rather
// than building a recursive tree viewer nothing here needs.
const flattenEvidence = (entries)=>entries.filter(([, v])=>!isEmptyEvidenceValue(v)).flatMap(([k, v])=>v !== null && typeof v === 'object' && !Array.isArray(v) ? Object.entries(v).filter(([, nested])=>!isEmptyEvidenceValue(nested)).map((pair)=>[
                `${k}.${pair[0]}`,
                pair[1]
            ]) : [
            [
                k,
                v
            ]
        ]);
// snake_case (or dotted, post-flatten) key -> "Spaced words" so it reads as
// English next to the callout fields instead of a raw JSON key.
const humaniseEvidenceKey = (k)=>{
    const text = k.replace(/\./g, ' ').split('_').filter(Boolean).join(' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
};
// Arrays (pws_sections, alternative_titles) join as their members, booleans
// read as a yes/no statement rather than bare true/false, everything else
// stringifies plainly.
const formatEvidenceValue = (v)=>{
    if (Array.isArray(v)) return v.map((item)=>String(item)).join(', ');
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    return String(v);
};
// C2-09: the chain is newest-first, so "previous" is the next row in the
// array. Diffing the stored id/text fields (not the rendered names) catches a
// company/vehicle swap even when the two resolve to the same display label.
const CHANGED_FIELD_LABELS = [
    [
        'title',
        'title'
    ],
    [
        'description',
        'description'
    ],
    [
        'qualifications',
        'qualifications'
    ],
    [
        'clearance',
        'clearance'
    ],
    [
        'certifications',
        'certifications'
    ],
    [
        'companyProfileId',
        'company'
    ],
    [
        'contractVehicleId',
        'vehicle'
    ]
];
const changedFields = (current, previous)=>previous ? CHANGED_FIELD_LABELS.filter(([key])=>current[key] !== previous[key]).map(([, label])=>label) : [];
const NONE = '__none__';
// Filter sentinel distinct from NONE above: NONE on the entry form means "no
// company/vehicle attribution" (a real stored state); ANY_FILTER on a catalog
// filter means "don't narrow" (nothing to pass to the server at all).
const ANY_FILTER = '__any__';
const lcatDefaults = (e)=>({
        id: e?.id ?? '',
        title: e?.title ?? '',
        description: e?.description ?? '',
        qualifications: e?.qualifications ?? '',
        clearance: e?.clearance ?? '',
        certifications: e?.certifications ?? '',
        companyProfileId: e?.companyProfileId || NONE,
        contractVehicleId: e?.contractVehicleId || NONE
    });
const isRetired = (e)=>e.status === 'RETIRED';
const entryColumns = [
    {
        key: 'title',
        header: 'Title'
    },
    {
        key: 'companyName',
        header: 'Company',
        render: (e)=>e.companyName || '—'
    },
    {
        key: 'contractVehicleName',
        header: 'Vehicle',
        render: (e)=>e.contractVehicleName || '—'
    },
    {
        key: 'clearance',
        header: 'Clearance',
        render: (e)=>e.clearance || '—'
    },
    {
        key: 'version',
        header: 'Version',
        render: (e)=>`v${e.version}`
    },
    {
        key: 'source',
        header: 'Source',
        render: (e)=>e.source === 'CANDIDATE' ? 'Extraction' : 'Manual'
    },
    {
        key: 'status',
        header: 'Status',
        render: (e)=>isRetired(e) ? /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "neutral",
                "data-testid": Testids.deliveryRegistries.lcatRetiredBadge,
                children: "Retired"
            }) : '—'
    }
];
export function LaborCategoriesTab() {
    const recordsEnabled = useRegistryRecordsEnabled();
    const rowOpen = useRowOpen('LCAT_ENTRY', ()=>setDetailMode('read'));
    const queryClient = useQueryClient();
    const tenantId = useTenantId();
    // SAD-948: the shared registries are edited only by area:registries holders.
    const canEdit = useTenantAreas().registries;
    const [recordParams] = useSearchParams();
    const navigate = useNavigate();
    const [view, setView] = useState(recordParams.has('mapping') ? 'mappings' : recordParams.get('view') === 'compare' ? 'compare' : 'catalog');
    const [mappingStatus, setMappingStatus] = useState('PENDING');
    const [reviewId, setReviewId] = useState(recordParams.get('mapping'));
    const [proposalResult, setProposalResult] = useState('');
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    // Own-company lens (06-phase-2b-followups): filter the catalog to entries
    // attributed to the tenant's own org profile. Company-less (as-written)
    // rows drop out while the lens is on — that is the lens's meaning.
    const [oursOnly, setOursOnly] = useState(false);
    const ownProfile = useOwnCompanyProfile().data ?? null;
    // C2-12 catalog filters: Vehicle and Company narrow the server query (the
    // same axes the hook already accepts); Origin narrows client-side since
    // MANUAL/CANDIDATE is a column on rows the server already returned. The
    // own-company lens outranks the Company filter — it is a company filter
    // itself, just one the lens picks rather than the reviewer.
    const [vehicleFilter, setVehicleFilter] = useState(ANY_FILTER);
    const [companyFilter, setCompanyFilter] = useState(ANY_FILTER);
    const [originFilter, setOriginFilter] = useState('ANY');
    // Retired categories are hidden from the catalog unless asked for.
    const [showRetired, setShowRetired] = useState(false);
    const entriesQuery = laborCategoryHooks.useList({
        companyId: tenantId,
        search: debouncedSearch,
        companyProfileId: oursOnly && ownProfile ? ownProfile.id : companyFilter === ANY_FILTER ? undefined : companyFilter,
        contractVehicleId: vehicleFilter === ANY_FILTER ? undefined : vehicleFilter,
        includeRetired: showRetired
    });
    const entries = (entriesQuery.data?.items ?? []).filter((e)=>originFilter === 'ANY' || e.source === originFilter);
    const paging = useShowMore(entries, JSON.stringify([
        debouncedSearch,
        vehicleFilter,
        companyFilter,
        originFilter,
        showRetired,
        oursOnly
    ]));
    // Unfiltered heads: the drawer derives its record here (so an open drawer
    // survives search/lens changes), the proposer's entry targets come from
    // here, and Compare resolves entry metadata here.
    // Retired heads stay resolvable here (an open drawer, a Compare column);
    // new-selection pickers filter them out below.
    const allEntriesQuery = laborCategoryHooks.useList({
        companyId: tenantId,
        includeRetired: true
    });
    const allEntries = allEntriesQuery.data?.items ?? [];
    // limit 1: only the tenant-wide DAT-13 lcat_mismatch signal count is wanted. The pill
    // shows only once this succeeded with a positive count — never while loading or errored.
    const mismatchQuery = useQuery({
        queryKey: [
            'labor category',
            'vehicle-mismatch-count',
            tenantId
        ],
        enabled: !!tenantId,
        retry: false,
        queryFn: async ()=>Number((await deliveryClient.listLaborCategoryEntries({
                companyId: tenantId,
                limit: 1
            })).vehicleMismatchSignalCount)
    });
    const vehicleMismatchCount = mismatchQuery.isSuccess ? mismatchQuery.data ?? 0 : 0;
    const selectableEntries = allEntries.filter((e)=>!isRetired(e));
    const upsert = laborCategoryHooks.useUpsert();
    const remove = laborCategoryHooks.useDelete();
    const retire = useRetireLaborCategoryEntry();
    const [retiring, setRetiring] = useState(null);
    const candidatesQuery = useLaborCategoryCandidates(tenantId, 'PENDING');
    const candidates = candidatesQuery.data ?? [];
    const acceptedCandidates = useLaborCategoryCandidates(tenantId, 'ACCEPTED').data ?? [];
    const accept = useAcceptLaborCategoryCandidate();
    const reject = useRejectLaborCategoryCandidate();
    // Rosetta: anchors feed the drawer's proposer and the Compare view.
    const anchorsQuery = useLaborCategoryAnchors(tenantId);
    const anchors = anchorsQuery.data ?? [];
    const propose = useProposeLaborCategoryMapping();
    // C2-05 inspect surface: a master-detail panel IN the Review queue tab, not
    // a second drawer — selecting a row never loses the reviewer's place in the
    // queue (the table stays rendered, the panel sits beneath it). Closing just
    // clears the selection; a decision that removes the candidate from the
    // PENDING list closes the panel for free, since selectedCandidate goes null.
    const [selectedCandidateId, setSelectedCandidateId] = useState(null);
    const selectedCandidate = candidates.find((c)=>c.id === selectedCandidateId) ?? null;
    useEffect(()=>{
        if (!selectedCandidateId) return;
        const onKey = (e)=>{
            if (e.key === 'Escape') setSelectedCandidateId(null);
        };
        document.addEventListener('keydown', onKey);
        return ()=>document.removeEventListener('keydown', onKey);
    }, [
        selectedCandidateId
    ]);
    // evidence_json is best-effort (zou's feeder writes it; hand-proposed/legacy
    // rows may have none) — malformed or missing must read as "no evidence",
    // never throw.
    const selectedEvidence = (()=>{
        if (!selectedCandidate?.evidenceJson) return null;
        try {
            const parsed = JSON.parse(selectedCandidate.evidenceJson);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
        } catch  {
            return null;
        }
    })();
    const remainingEvidence = selectedEvidence ? flattenEvidence(Object.entries(selectedEvidence).filter(([k])=>!EVIDENCE_CALLOUT_KEYS.includes(k))) : [];
    // Compare: pick an anchor, see every company's APPROVED-mapped entry side
    // by side — the Rosetta payoff view, pre-rates.
    const [compareAnchorId, setCompareAnchorId] = useState(recordParams.get('anchor') ?? '');
    const compareQuery = useLaborCategoryMappings(tenantId, {
        toAnchorId: compareAnchorId
    }, {
        enabled: view === 'compare' && !!compareAnchorId
    });
    const compareMappings = compareQuery.data ?? [];
    useEffect(()=>{
        if (recordParams.get('view') === 'compare') {
            setView('compare');
            setCompareAnchorId(recordParams.get('anchor') ?? '');
        } else if (recordParams.has('mapping')) {
            setView('mappings');
            setReviewId(recordParams.get('mapping'));
        }
    }, [
        recordParams
    ]);
    // Drawer Mappings section state: existing edges for the open entry + the
    // proposer. Target is EXACTLY ONE of a SOC anchor or another entry (the
    // dedup move) — mirrors the one-target CHECK on the table.
    const [mapTargetKind, setMapTargetKind] = useState('ANCHOR');
    const [mapAnchorId, setMapAnchorId] = useState('');
    const [mapEntryId, setMapEntryId] = useState('');
    const [mapType, setMapType] = useState('EQUIVALENT');
    const [mapEvidence, setMapEvidence] = useState('');
    // Company/vehicle pickers on the create/edit form — the shared catalogs.
    const companiesQuery = companyProfileHooks.useList({
        companyId: tenantId
    });
    const vehiclesQuery = contractVehicleHooks.useList({
        companyId: tenantId
    });
    const companyOptions = [
        {
            value: NONE,
            label: 'None (government / as-written)'
        },
        ...(companiesQuery.data?.items ?? []).map((c)=>({
                value: c.id,
                label: c.name
            }))
    ];
    const vehicleOptions = [
        {
            value: NONE,
            label: 'None'
        },
        ...(vehiclesQuery.data?.items ?? []).map((v)=>({
                value: v.id,
                label: v.name
            }))
    ];
    const fields = [
        {
            kind: 'text',
            name: 'title',
            label: 'Title (as written)',
            required: true,
            maxLength: 255
        },
        {
            kind: 'select',
            name: 'companyProfileId',
            label: 'Company',
            options: companyOptions
        },
        {
            kind: 'select',
            name: 'contractVehicleId',
            label: 'Vehicle',
            options: vehicleOptions
        },
        {
            kind: 'textarea',
            name: 'description',
            label: 'Description',
            rows: 3
        },
        {
            kind: 'textarea',
            name: 'qualifications',
            label: 'Qualifications',
            rows: 3
        },
        {
            kind: 'text',
            name: 'clearance',
            label: 'Clearance',
            maxLength: 100
        },
        {
            kind: 'textarea',
            name: 'certifications',
            label: 'Certifications',
            rows: 2
        }
    ];
    const [formOpen, setFormOpen] = useState(false);
    // Derive the drawer record from the UNFILTERED list by id — never sync into
    // state, and never let the search box or the own-company lens close an
    // already-open drawer.
    // ?record= opens the canonical record (Rate Book identity links land here).
    const { recordId: detailId, openRecord, closeRecord } = useRegistryRecord();
    const setDetailId = (id)=>id ? openRecord(id) : closeRecord();
    const historyQuery = useLaborCategoryHistory(detailId, tenantId);
    const history = historyQuery.data ?? [];
    const detail = detailId ? allEntries.find((e)=>e.id === detailId) ?? history.find((e)=>e.id === detailId) ?? null : null;
    // Reset the proposer whenever the drawer moves to a different entry: a
    // target picked for entry A must never ride along and end up proposed from
    // entry C's drawer — that queue row would look perfectly well-formed to
    // the reviewer (#997 review finding).
    useEffect(()=>{
        setMapTargetKind('ANCHOR');
        setMapAnchorId('');
        setMapEntryId('');
        setMapType('EQUIVALENT');
        setMapEvidence('');
        setProposalResult('');
        setRateType('CEILING');
        setRateAmount('');
        setRatePeriod('');
        setRateBurden('UNKNOWN');
        setRateUnit(UNIT_NOT_STATED);
    }, [
        detailId
    ]);
    const [detailMode, setDetailMode] = useState('read');
    // Rate Book (Phase 4 slice 1). The drawer asks the LOGICAL question
    // (across_versions) because a rate pins the entry version that was current
    // when it was observed; Compare reads the whole book once and groups by
    // entry so each column can show its own typed rates.
    const drawerRatesQuery = useRateObservations(tenantId, {
        lcatEntryId: detailId ?? '',
        acrossVersions: true,
        includeCorrected: true
    }, {
        enabled: !!detailId
    });
    const drawerRates = drawerRatesQuery.data ?? [];
    const createRate = useCreateRateObservation();
    const [rateType, setRateType] = useState('CEILING');
    const [rateAmount, setRateAmount] = useState('');
    const [ratePeriod, setRatePeriod] = useState('');
    const [rateBurden, setRateBurden] = useState('UNKNOWN');
    // Lets a curator STATE the unit rather than have it silently assumed
    // (C4-02, part D) — defaults to hourly, the common case the shared hook
    // already covers.
    const [rateUnit, setRateUnit] = useState(UNIT_NOT_STATED);
    const compareRatesQuery = useRateObservations(tenantId, {
        limit: 500
    }, {
        enabled: view === 'compare' && !!compareAnchorId
    });
    const [deleting, setDeleting] = useState(null);
    // Rosetta directionality (SAD-441): one stored decision per pair, read from
    // either endpoint. The server filters by entry_id (from OR to), so the drawer
    // asks for just its own edges; reversal is a read-time view
    // (mappingDirection.ts), never a second approval. The catalog's
    // Mapped/Pending/Unmapped column needs every row's state at once, so it
    // reads the tenant-wide list once rather than one query per row.
    const allMappingsQuery = useLaborCategoryMappings(tenantId, {});
    const allMappings = allMappingsQuery.data ?? [];
    const reviewMapping = allMappings.find((m)=>m.id === reviewId);
    const entryMappingsQuery = useLaborCategoryMappings(tenantId, {
        entryId: detail?.id
    }, {
        enabled: !!detail
    });
    const entryMappings = detail ? entryMappingsQuery.data ?? [] : [];
    const displayMappings = detail ? entryMappings.map((m)=>({
            key: m.id,
            status: m.status,
            ...mappingViewFrom(m, detail.id, detail.title)
        })) : [];
    // Dedup guard: a pair already covered by a pending/approved edge in EITHER
    // direction already carries the reverse reading for free — proposing again
    // would just ask for a second approval of one decision (the server refuses
    // the mirror edge too).
    const duplicateMapping = (()=>{
        if (!detail) return undefined;
        if (mapTargetKind === 'ANCHOR') {
            if (!mapAnchorId) return undefined;
            return entryMappings.find((m)=>(m.status === 'PENDING' || m.status === 'APPROVED') && m.fromEntryId === detail.id && m.toAnchorId === mapAnchorId);
        }
        if (!mapEntryId) return undefined;
        return entryMappings.find((m)=>(m.status === 'PENDING' || m.status === 'APPROVED') && (m.fromEntryId === detail.id && m.toEntryId === mapEntryId || m.fromEntryId === mapEntryId && m.toEntryId === detail.id));
    })();
    const submitEntry = async (values, id)=>{
        const saved = await upsert.mutateAsync({
            id,
            companyId: tenantId,
            companyProfileId: values.companyProfileId === NONE ? '' : values.companyProfileId ?? '',
            contractVehicleId: values.contractVehicleId === NONE ? '' : values.contractVehicleId ?? '',
            title: values.title,
            description: values.description ?? '',
            qualifications: values.qualifications ?? '',
            clearance: values.clearance ?? '',
            certifications: values.certifications ?? ''
        });
        // An edit mints a NEW version row with a new id — follow the drawer to it
        // so the open detail keeps pointing at the current head.
        if (id && saved && saved.id !== id) {
            setDetailId(saved.id);
        }
        await queryClient.invalidateQueries({
            queryKey: [
                'lcat mappings'
            ]
        });
    };
    const detailRetired = !!detail && isRetired(detail);
    const detailEdit = detail && !detailRetired ? {
        defaultValues: lcatDefaults(detail),
        onSubmit: (v)=>submitEntry(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save as new version'
    } : undefined;
    const detailSections = detail ? [
        {
            heading: 'Labor category (as written)',
            rows: [
                {
                    label: 'Title',
                    value: detail.title,
                    editField: {
                        kind: 'text',
                        name: 'title',
                        label: 'Title (as written)',
                        required: true,
                        maxLength: 255
                    }
                },
                {
                    label: 'Company',
                    value: detail.companyName || '—',
                    editField: {
                        kind: 'select',
                        name: 'companyProfileId',
                        label: 'Company',
                        options: companyOptions
                    }
                },
                {
                    label: 'Vehicle',
                    value: detail.contractVehicleName || '—',
                    editField: {
                        kind: 'select',
                        name: 'contractVehicleId',
                        label: 'Vehicle',
                        options: vehicleOptions
                    }
                },
                {
                    label: 'Description',
                    value: detail.description || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'description',
                        label: 'Description',
                        rows: 3
                    }
                },
                {
                    label: 'Qualifications',
                    value: detail.qualifications || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'qualifications',
                        label: 'Qualifications',
                        rows: 3
                    }
                },
                {
                    label: 'Clearance',
                    value: detail.clearance || '—',
                    editField: {
                        kind: 'text',
                        name: 'clearance',
                        label: 'Clearance',
                        maxLength: 100
                    }
                },
                {
                    label: 'Certifications',
                    value: detail.certifications || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'certifications',
                        label: 'Certifications',
                        rows: 2
                    }
                },
                // Derived facts — read-only in edit mode too.
                {
                    label: 'Version',
                    value: `v${detail.version}`
                },
                {
                    label: 'Source',
                    value: detail.source === 'CANDIDATE' ? 'Extraction candidate' : 'Manual'
                }
            ]
        }
    ] : [];
    const candidateColumns = [
        {
            key: 'title',
            header: 'Title',
            render: (c)=>/*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex items-center gap-2",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: cn(c.id === selectedCandidateId && 'font-semibold text-foreground'),
                            children: c.title
                        }),
                        c.vehicleMismatch && /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "warning",
                            "data-testid": Testids.deliveryRegistries.lcatCandidateMismatchBadge,
                            title: `Similar entries exist on ${c.mismatchVehicleName || 'a specific vehicle'} — accepting keeps this solicitation-scoped rather than minting it into that vehicle's catalog`,
                            children: "Vehicle mismatch"
                        })
                    ]
                })
        },
        {
            key: 'companyName',
            header: 'Company',
            render: (c)=>c.companyName || '—'
        },
        {
            key: 'contractVehicleName',
            header: 'Vehicle',
            render: (c)=>c.contractVehicleName || '—'
        },
        {
            key: 'clearance',
            header: 'Clearance',
            render: (c)=>c.clearance || '—'
        },
        {
            key: 'candidateKey',
            header: 'From',
            // The feeder-owned key doubles as a provenance hint (zou:<project>:<code>).
            render: (c)=>c.zouProjectId || c.candidateKey || '—'
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-end justify-between gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraTabBar, {
                        idBase: "lcat-views",
                        className: "min-w-0 flex-1",
                        label: "Labor category views",
                        value: view,
                        onValueChange: (next)=>{
                            // With records on, Review queue and Compare leave for their pages
                            // (the queue, the selected anchor); Compare with no anchor stays
                            // here as the anchor picker.
                            if (recordsEnabled && next === 'candidates') {
                                navigate('/registries/review-queue?kind=LCAT_CANDIDATE');
                            } else if (recordsEnabled && next === 'compare' && compareAnchorId) {
                                navigate(`/registries/labor-categories/${encodeURIComponent(compareAnchorId)}`);
                            } else {
                                setView(next);
                            }
                        },
                        options: [
                            {
                                value: 'catalog',
                                label: 'Catalog',
                                testId: Testids.deliveryRegistries.lcatViewCatalog
                            },
                            {
                                value: 'candidates',
                                label: 'Review queue',
                                count: candidates.length > 0 ? candidates.length : undefined,
                                testId: Testids.deliveryRegistries.lcatViewCandidates
                            },
                            {
                                value: 'mappings',
                                label: 'Mappings',
                                testId: Testids.deliveryRegistries.lcatViewMappings
                            },
                            {
                                value: 'compare',
                                label: 'Compare',
                                testId: Testids.deliveryRegistries.lcatViewCompare
                            }
                        ]
                    }),
                    vehicleMismatchCount > 0 && /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: "neutral",
                        "data-testid": Testids.deliveryRegistries.lcatVehicleMismatchPill,
                        children: [
                            "Vehicle mismatch (",
                            vehicleMismatchCount,
                            ")"
                        ]
                    }),
                    canEdit && view === 'catalog' && /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>setFormOpen(true),
                        "data-testid": Testids.deliveryRegistries.lcatCreate,
                        children: "New Labor Category"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                active: view === 'catalog',
                idBase: "lcat-views",
                value: "catalog",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center gap-4",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraInput, {
                                "data-testid": Testids.deliveryRegistries.lcatSearchInput,
                                placeholder: "Search by title",
                                value: search,
                                onChange: (e)=>setSearch(e.target.value),
                                className: "max-w-sm"
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: vehicleFilter,
                                onValueChange: setVehicleFilter,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        className: "w-44",
                                        "aria-label": "Filter by vehicle",
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: ANY_FILTER,
                                                children: "All vehicles"
                                            }),
                                            (vehiclesQuery.data?.items ?? []).map((v)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: v.id,
                                                    children: v.name
                                                }, v.id))
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: companyFilter,
                                onValueChange: setCompanyFilter,
                                disabled: oursOnly,
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        className: "w-44",
                                        "aria-label": "Filter by company",
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: ANY_FILTER,
                                                children: "All companies"
                                            }),
                                            (companiesQuery.data?.items ?? []).map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: c.id,
                                                    children: c.name
                                                }, c.id))
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Select, {
                                value: originFilter,
                                onValueChange: (v)=>setOriginFilter(v),
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        className: "w-40",
                                        "aria-label": "Filter by origin",
                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                    }),
                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: "ANY",
                                                children: "Any origin"
                                            }),
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: "MANUAL",
                                                children: "Manual"
                                            }),
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: "CANDIDATE",
                                                children: "Extraction"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                className: "cursor-pointer items-center text-xs text-muted-foreground",
                                "data-testid": Testids.deliveryRegistries.lcatShowRetiredToggle,
                                label: "Show retired",
                                controlProps: {
                                    checked: showRetired,
                                    onCheckedChange: (checked)=>setShowRetired(checked === true)
                                }
                            }),
                            ownProfile && /*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                className: "cursor-pointer items-center text-xs text-muted-foreground",
                                "data-testid": Testids.deliveryRegistries.lcatOwnCompanyToggle,
                                label: `${ownProfile.name} only`,
                                controlProps: {
                                    checked: oursOnly,
                                    onCheckedChange: (checked)=>setOursOnly(checked === true)
                                }
                            })
                        ]
                    }),
                    recordsEnabled && entriesQuery.isError && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        role: "alert",
                        children: "Unable to load labor categories."
                    }),
                    /*#__PURE__*/ _jsx(EntityTable, {
                        data: recordsEnabled ? paging.visible : entries,
                        columns: recordsEnabled ? [
                            ...entryColumns.map((column)=>column.key === 'title' ? {
                                    ...column,
                                    header: 'Title, as written',
                                    render: (e)=>/*#__PURE__*/ _jsx(Link, {
                                            to: registryHref({
                                                kind: 'LCAT_ENTRY',
                                                id: e.id
                                            }, `/registries${recordParams.size ? `?${recordParams}` : ''}`) ?? '#',
                                            children: e.title
                                        })
                                } : column.key === 'source' ? {
                                    ...column,
                                    render: (e)=>e.sourceLabel || '—'
                                } : column),
                            {
                                key: 'matchedTo',
                                header: 'Matched to',
                                render: (e)=>{
                                    const mapping = e.primaryMapping;
                                    const status = e.vehicleMismatch ? 'VEHICLE_MISMATCH' : mapping?.status ?? '';
                                    const label = e.vehicleMismatch ? 'Vehicle mismatch' : status === 'APPROVED' ? mapping?.anchorTitle || 'Unmatched' : [
                                        'PENDING',
                                        'REVIEW_NEEDED'
                                    ].includes(status) ? 'Needs review' : 'Unmatched';
                                    return /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: mappingVariant(status, mapping?.relation),
                                        children: label
                                    });
                                }
                            }
                        ] : [
                            ...entryColumns,
                            {
                                key: 'mappingState',
                                header: 'Mapping',
                                // C3-04: Unmapped is a valid, neutral condition — a specialized
                                // role can legitimately stay here, so it gets the same neutral
                                // tone as every other state, never a warning colour.
                                render: (e)=>{
                                    const state = mappingStateFor(e.id, allMappings);
                                    return /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: state === 'MAPPED' ? 'success' : state === 'PENDING_REVIEW' ? 'warning' : 'neutral',
                                        children: MAPPING_STATE_LABELS[state]
                                    });
                                }
                            }
                        ],
                        testid: Testids.deliveryRegistries.lcatTable,
                        rowTestid: Testids.deliveryRegistries.lcatRow,
                        rowKey: (e)=>e.id,
                        isLoading: entriesQuery.isLoading,
                        onRowClick: (row)=>rowOpen.onRowClick(row.id),
                        hideRowActions: !canEdit && !recordsEnabled,
                        rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2 justify-end",
                                children: [
                                    rowOpen.quickLookAction(row.id),
                                    canEdit && /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            !isRetired(row) && /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "ghost",
                                                onClick: ()=>{
                                                    setDetailMode('edit');
                                                    setDetailId(row.id);
                                                },
                                                children: "Edit"
                                            }),
                                            !isRetired(row) && /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "ghost",
                                                "data-testid": Testids.deliveryRegistries.lcatRetire,
                                                onClick: ()=>setRetiring(row),
                                                children: "Retire"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "ghost",
                                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                                onClick: ()=>setDeleting(row),
                                                children: "Delete"
                                            })
                                        ]
                                    })
                                ]
                            }),
                        emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                            title: "No labor categories yet",
                            description: "Create entries by hand, or accept extraction candidates from the Review queue — every company's LCATs as written, browsable by company and vehicle."
                        })
                    }),
                    recordsEnabled && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            paging.hasMore && /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                "data-testid": Testids.deliveryRegistries.showMore,
                                onClick: paging.showMore,
                                children: "Show more"
                            }),
                            /*#__PURE__*/ _jsx(RegistryListNote, {
                                children: "Every company’s title is kept exactly as written, then matched to a shared occupation by a person. Click a title for everyone’s version and rates."
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                active: view === 'candidates',
                idBase: "lcat-views",
                value: "candidates",
                children: [
                    /*#__PURE__*/ _jsx(EntityTable, {
                        data: candidates,
                        columns: candidateColumns,
                        testid: Testids.deliveryRegistries.lcatCandidatesTable,
                        rowTestid: Testids.deliveryRegistries.lcatCandidateRow,
                        rowKey: (c)=>c.id,
                        isLoading: candidatesQuery.isLoading,
                        onRowClick: (row)=>setSelectedCandidateId(row.id),
                        hideRowActions: !canEdit,
                        rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                                className: "flex gap-2 justify-end",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        onClick: ()=>accept.mutate(row.id),
                                        disabled: accept.isPending || reject.isPending,
                                        "data-testid": Testids.deliveryRegistries.lcatCandidateAccept,
                                        children: "Accept"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "destructive",
                                        onClick: ()=>reject.mutate(row.id),
                                        disabled: accept.isPending || reject.isPending,
                                        "data-testid": Testids.deliveryRegistries.lcatCandidateReject,
                                        children: "Reject"
                                    })
                                ]
                            }),
                        emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                            title: "Review queue is empty",
                            description: "Extraction pipelines file labor-category suggestions here with their evidence; accepting one adds it to the catalog."
                        })
                    }),
                    selectedCandidate && /*#__PURE__*/ _jsxs("div", {
                        className: "mt-4 space-y-4 rounded-md border border-border p-4",
                        "data-testid": Testids.deliveryRegistries.lcatCandidateInspectPanel,
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-start justify-between gap-3",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm font-semibold",
                                        children: selectedCandidate.title
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        onClick: ()=>setSelectedCandidateId(null),
                                        "data-testid": Testids.deliveryRegistries.lcatCandidateInspectClose,
                                        children: "Close"
                                    })
                                ]
                            }),
                            selectedCandidate.vehicleMismatch && /*#__PURE__*/ _jsxs(SpecraBadge, {
                                variant: "warning",
                                "data-testid": Testids.deliveryRegistries.lcatCandidateMismatchBadge,
                                children: [
                                    "Vehicle mismatch — similar entries exist on",
                                    ' ',
                                    selectedCandidate.mismatchVehicleName || 'a specific vehicle',
                                    "; accepting keeps this solicitation-scoped rather than minting it into that vehicle's catalog"
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                "data-testid": Testids.deliveryRegistries.lcatCandidateInspectDefinition,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-[length:var(--fs-ui-label)] font-medium uppercase tracking-[var(--ls-ui-eyebrow)] text-muted-foreground",
                                        children: "Proposed definition"
                                    }),
                                    [
                                        [
                                            'Title',
                                            selectedCandidate.title
                                        ],
                                        [
                                            'Description',
                                            selectedCandidate.description
                                        ],
                                        [
                                            'Qualifications',
                                            selectedCandidate.qualifications
                                        ],
                                        [
                                            'Clearance',
                                            selectedCandidate.clearance
                                        ],
                                        [
                                            'Certifications',
                                            selectedCandidate.certifications
                                        ]
                                    ].map(([label, value])=>/*#__PURE__*/ _jsxs("div", {
                                            className: "text-[length:var(--fs-ui-body)]",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "text-muted-foreground",
                                                    children: [
                                                        label,
                                                        ": "
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "whitespace-pre-wrap",
                                                    children: value || 'Not stated in the source'
                                                })
                                            ]
                                        }, label))
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                "data-testid": Testids.deliveryRegistries.lcatCandidateInspectAttribution,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-[length:var(--fs-ui-label)] font-medium uppercase tracking-[var(--ls-ui-eyebrow)] text-muted-foreground",
                                        children: "Attribution"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                                        children: [
                                            selectedCandidate.companyName || 'No company attribution',
                                            " ·",
                                            ' ',
                                            selectedCandidate.contractVehicleName || 'No vehicle recorded'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                                        children: [
                                            selectedCandidate.zouProjectId ? `Zou project: ${selectedCandidate.zouProjectId}` : 'Not linked to a zou project',
                                            ' · ',
                                            selectedCandidate.solicitationRef ? `Solicitation: ${selectedCandidate.solicitationRef}` : 'No solicitation reference recorded'
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                "data-testid": Testids.deliveryRegistries.lcatCandidateInspectSource,
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-[length:var(--fs-ui-label)] font-medium uppercase tracking-[var(--ls-ui-eyebrow)] text-muted-foreground",
                                        children: "Source evidence"
                                    }),
                                    selectedCandidate.provenanceRedacted && /*#__PURE__*/ _jsx("p", {
                                        className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                                        "data-testid": Testids.deliveryRegistries.lcatCandidateProvenanceRestricted,
                                        children: "Source provenance restricted"
                                    }),
                                    selectedEvidence && Object.keys(selectedEvidence).length > 0 ? /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-1 text-[length:var(--fs-ui-body)]",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "text-muted-foreground",
                                                children: [
                                                    selectedEvidence.source_filename ? `File: ${String(selectedEvidence.source_filename)}` : null,
                                                    selectedEvidence.source_document_id ? `Document: ${String(selectedEvidence.source_document_id)}` : null,
                                                    selectedEvidence.page_number != null ? `Page ${String(selectedEvidence.page_number)}` : null,
                                                    selectedEvidence.section_number ? `Section ${String(selectedEvidence.section_number)}` : null
                                                ].filter(Boolean).join(' · ') || 'No document location recorded'
                                            }),
                                            typeof selectedEvidence.exact_text === 'string' && selectedEvidence.exact_text && /*#__PURE__*/ _jsxs("blockquote", {
                                                className: "border-l-2 border-border pl-2 italic text-muted-foreground",
                                                "data-testid": Testids.deliveryRegistries.lcatCandidateInspectExcerpt,
                                                children: [
                                                    "“",
                                                    selectedEvidence.exact_text,
                                                    "”"
                                                ]
                                            }),
                                            typeof selectedEvidence.confidence === 'number' && /*#__PURE__*/ _jsxs("div", {
                                                className: "text-muted-foreground",
                                                children: [
                                                    "Confidence in this suggestion: ",
                                                    Math.round(selectedEvidence.confidence * 100),
                                                    "%"
                                                ]
                                            }),
                                            remainingEvidence.length > 0 && /*#__PURE__*/ _jsx("div", {
                                                className: "text-muted-foreground",
                                                children: remainingEvidence.map(([k, v])=>`${humaniseEvidenceKey(k)}: ${formatEvidenceValue(v)}`).join(' · ')
                                            })
                                        ]
                                    }) : /*#__PURE__*/ _jsx("p", {
                                        className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                                        children: selectedCandidate.provenanceRedacted ? 'Source details are hidden.' : 'No source evidence recorded for this candidate.'
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(CandidateDecisionPanel, {
                                candidate: selectedCandidate,
                                entries: selectableEntries,
                                companies: companiesQuery.data?.items ?? [],
                                vehicles: vehiclesQuery.data?.items ?? [],
                                canEdit: canEdit,
                                busy: accept.isPending || reject.isPending,
                                onAccept: (args)=>accept.mutate(args),
                                onReject: (id)=>reject.mutate(id)
                            }, selectedCandidate.id)
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                keepMounted: !!reviewId,
                active: view === 'mappings',
                idBase: "lcat-views",
                value: "mappings",
                children: [
                    /*#__PURE__*/ _jsxs(Select, {
                        value: mappingStatus,
                        onValueChange: setMappingStatus,
                        children: [
                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                "aria-label": "Mapping status",
                                "data-testid": Testids.deliveryRegistries.lcatMappingStatus,
                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                            }),
                            /*#__PURE__*/ _jsx(SelectContent, {
                                children: [
                                    'PENDING',
                                    'REVIEW_NEEDED',
                                    'APPROVED',
                                    'REJECTED',
                                    'WITHDRAWN'
                                ].map((status)=>/*#__PURE__*/ _jsx(SelectItem, {
                                        value: status,
                                        children: status
                                    }, status))
                            })
                        ]
                    }),
                    allMappingsQuery.isError && /*#__PURE__*/ _jsx("p", {
                        role: "alert",
                        children: "Unable to load mapping history. Refresh to retry."
                    }),
                    reviewId && reviewMapping && /*#__PURE__*/ _jsx(MappingReviewPanel, {
                        companyId: tenantId,
                        initialMapping: reviewMapping,
                        entries: selectableEntries,
                        anchors: anchors,
                        history: allMappings,
                        canEdit: canEdit,
                        onOpenSource: (id)=>{
                            setDetailId(id);
                            setDetailMode('read');
                        },
                        onClose: ()=>setReviewId(null),
                        onRefresh: async ()=>{
                            const refreshed = await allMappingsQuery.refetch();
                            if (refreshed.isError) throw refreshed.error;
                            return refreshed.data?.find((m)=>m.id === reviewId);
                        }
                    }, reviewId),
                    /*#__PURE__*/ _jsx(EntityTable, {
                        data: allMappings.filter((m)=>m.status === mappingStatus),
                        columns: [
                            {
                                key: 'fromEntryTitle',
                                header: 'Entry'
                            },
                            {
                                // C3-02: a readable statement naming both endpoints, not a bare
                                // relation label beside a separate target cell.
                                key: 'relationship',
                                header: 'Relationship',
                                render: (m)=>mappingStatement(m)
                            },
                            {
                                // Confidence in the SUGGESTION, never a post-approval
                                // equivalence percentage — the header says so (C3-06).
                                key: 'confidence',
                                header: 'Suggestion confidence',
                                render: (m)=>m.confidence !== undefined ? `${Math.round(m.confidence * 100)}%` : '—'
                            },
                            {
                                key: 'evidence',
                                header: 'Evidence',
                                render: (m)=>{
                                    const summary = evidenceSummary(m.evidenceJson);
                                    return /*#__PURE__*/ _jsx("span", {
                                        className: "block max-w-72 truncate text-muted-foreground",
                                        title: summary === '—' ? undefined : m.evidenceJson,
                                        children: summary
                                    });
                                }
                            }
                        ],
                        testid: Testids.deliveryRegistries.lcatMappingsTable,
                        rowTestid: Testids.deliveryRegistries.lcatMappingRow,
                        rowKey: (m)=>m.id,
                        isLoading: allMappingsQuery.isLoading,
                        rowActions: (row)=>/*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                onClick: ()=>setReviewId(row.id),
                                "data-testid": Testids.deliveryRegistries.lcatMappingOpen,
                                children: "Review mapping"
                            }),
                        emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                            title: `No ${mappingStatus.toLowerCase()} mappings`,
                            description: "Propose mappings from an entry's drawer — equivalences are reviewed here, never auto-approved, never transitive."
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                active: view === 'compare',
                idBase: "lcat-views",
                value: "compare",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsx(Link, {
                            to: rateBookHref({
                                lcat: allEntries.find((e)=>e.id === recordParams.get('entry'))?.id
                            }),
                            className: "inline-block text-xs text-primary underline-offset-2 hover:underline",
                            "data-testid": Testids.deliveryRegistries.rateBookOpen,
                            children: "Open in Rate Book"
                        }),
                        /*#__PURE__*/ _jsxs(Select, {
                            value: compareAnchorId,
                            onValueChange: (id)=>recordsEnabled ? navigate(`/registries/labor-categories/${encodeURIComponent(id)}`) : setCompareAnchorId(id),
                            children: [
                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                    className: "max-w-md",
                                    "aria-label": "Occupational anchor",
                                    "data-testid": Testids.deliveryRegistries.lcatCompareAnchorSelect,
                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                        placeholder: "Pick an occupational anchor…"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SelectContent, {
                                    children: anchors.map((a)=>/*#__PURE__*/ _jsxs(SelectItem, {
                                            value: a.id,
                                            children: [
                                                a.socCode,
                                                " — ",
                                                a.title
                                            ]
                                        }, a.id))
                                })
                            ]
                        }),
                        compareAnchorId === '' ? /*#__PURE__*/ _jsx(EmptyState, {
                            title: "Side-by-side comparison",
                            // C3-03/PRO-C-05: sharing an anchor groups entries under one
                            // occupation — it is NOT a pairwise equivalence claim between
                            // the columns shown. Say the grouping, not an equivalence.
                            description: "Pick a SOC anchor to see labor categories grouped under the same occupation, side by side — original text preserved, no fake normalization. Grouping under an anchor is not a direct equivalence between the columns; only an approved entry-to-entry mapping is."
                        }) : /*#__PURE__*/ _jsx(LcatCompareView, {
                            mappings: compareMappings,
                            mappingsQuery: compareQuery,
                            rates: compareRatesQuery.data ?? [],
                            ratesQuery: compareRatesQuery,
                            entries: allEntries,
                            entriesQuery: allEntriesQuery,
                            candidates: acceptedCandidates,
                            contextEntryId: recordParams.get('entry') ?? '',
                            typeLabels: ALL_RATE_TYPE_LABELS,
                            formatAmount: formatRateAmount
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Create Labor Category",
                // C2-03/C2-10: this writes a SHARED, tenant-wide definition, not a
                // local working role — say so, since nothing else in the dialog hints
                // at the blast radius of hitting Save.
                description: "Creates a shared definition visible across the whole tenant catalog — a deliberate action, not a side effect of typing a role into a staffing row. Entered by hand with no source attached, it stays honestly Manual.",
                fields: fields,
                defaultValues: lcatDefaults(null),
                onSubmit: async (v)=>{
                    await submitEntry(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsxs(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? detail.title : '',
                sections: detailSections,
                testid: Testids.deliveryRegistries.lcatDetailDrawer,
                extraHeaderAction: recordsEnabled && detail ? /*#__PURE__*/ _jsx(OpenFullPage, {
                    kind: "LCAT_ENTRY",
                    id: detail.id
                }) : undefined,
                edit: detailEdit,
                canEdit: canEdit && !detailRetired,
                initialMode: detailMode,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.deliveryRegistries.lcatMappingsSection,
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                children: "Mappings"
                            }),
                            displayMappings.map((m)=>/*#__PURE__*/ _jsxs("div", {
                                    className: "rounded-md border border-border/60 p-2 text-[12.5px]",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    variant: m.status === 'APPROVED' ? 'success' : m.status === 'REJECTED' ? 'danger' : 'warning',
                                                    children: m.status.toLowerCase()
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    children: m.statement
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            size: "sm",
                                            variant: "ghost",
                                            onClick: ()=>{
                                                setReviewId(m.key);
                                                setView('mappings');
                                                setDetailId(null);
                                            },
                                            "data-testid": Testids.deliveryRegistries.lcatMappingOpen,
                                            children: "Review mapping"
                                        }),
                                        m.note && /*#__PURE__*/ _jsx("p", {
                                            className: "text-[length:var(--fs-ui-label)] text-muted-foreground",
                                            children: m.note
                                        })
                                    ]
                                }, m.key)),
                            entryMappings.filter((m)=>m.toAnchorId && m.status === 'APPROVED').map((m)=>/*#__PURE__*/ _jsx(Link, {
                                    to: rosettaCompareHref(m.toAnchorId, detail?.id ?? ''),
                                    onClick: ()=>{
                                        setCompareAnchorId(m.toAnchorId);
                                        setView('compare');
                                        setDetailId(null);
                                    },
                                    children: "Compare occupational group with this definition"
                                }, m.id)),
                            displayMappings.length === 0 && !entryMappingsQuery.isLoading && // C3-04: Unmapped is a valid, neutral condition — not a defect or
                            // a chore to clear. A specialized role can legitimately stay here,
                            // and rates/staffing selection work the same regardless.
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "Unmapped. Nothing to fix — a specialized role can stay this way, and rates and staffing selection already work the same either way."
                            }),
                            duplicateMapping && // Deliverable 4: the pair already has a non-rejected edge in
                            // either direction — its reverse reading already exists above, so
                            // proposing again would just ask for a second approval of it.
                            /*#__PURE__*/ _jsxs("p", {
                                className: "text-[length:var(--fs-ui-body)] text-muted-foreground",
                                children: [
                                    "Already covered: ",
                                    mappingStatement(duplicateMapping),
                                    " (",
                                    duplicateMapping.status.toLowerCase(),
                                    "). The reverse reading already exists — no second approval needed."
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraTextarea, {
                                "aria-label": "Mapping evidence and rationale",
                                placeholder: "Evidence or rationale for this proposal / reconsideration",
                                value: mapEvidence,
                                onChange: (e)=>setMapEvidence(e.target.value),
                                "data-testid": Testids.deliveryRegistries.lcatMappingEvidence
                            }),
                            entryMappings.some((m)=>m.status === 'REJECTED') && /*#__PURE__*/ _jsx("p", {
                                children: "Earlier rejections are retained above. Reconsideration creates a new proposal only when the target, relation or evidence materially changes."
                            }),
                            proposalResult && /*#__PURE__*/ _jsx("output", {
                                children: proposalResult
                            }),
                            !detailRetired && canEdit && /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: mapType,
                                        onValueChange: setMapType,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 flex-1 min-w-36 text-xs",
                                                "aria-label": "Mapping relation",
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: Object.entries(MAPPING_TYPE_LABELS).map(([v, label])=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: v,
                                                        children: label
                                                    }, v))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: mapTargetKind,
                                        onValueChange: (v)=>setMapTargetKind(v),
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 flex-1 min-w-36 text-xs",
                                                "aria-label": "Target kind",
                                                "data-testid": Testids.deliveryRegistries.lcatMapTargetKindSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsxs(SelectContent, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectItem, {
                                                        value: "ANCHOR",
                                                        children: "SOC anchor"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectItem, {
                                                        value: "ENTRY",
                                                        children: "Labor category"
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    mapTargetKind === 'ANCHOR' ? /*#__PURE__*/ _jsxs(Select, {
                                        value: mapAnchorId,
                                        onValueChange: setMapAnchorId,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 flex-[2] min-w-52 text-xs",
                                                "aria-label": "Target anchor",
                                                "data-testid": Testids.deliveryRegistries.lcatMapToAnchorSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                                    placeholder: "Pick a SOC anchor…"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: anchors.map((a)=>/*#__PURE__*/ _jsxs(SelectItem, {
                                                        value: a.id,
                                                        children: [
                                                            a.socCode,
                                                            " — ",
                                                            a.title
                                                        ]
                                                    }, a.id))
                                            })
                                        ]
                                    }) : // The dedup move: assert this entry means the same thing as
                                    // another entry in the catalog (typically another vendor's
                                    // wording of the same job). Targets are current heads only,
                                    // never the entry itself.
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: mapEntryId,
                                        onValueChange: setMapEntryId,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 flex-[2] min-w-52 text-xs",
                                                "aria-label": "Target labor category",
                                                "data-testid": Testids.deliveryRegistries.lcatMapToEntrySelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {
                                                    placeholder: "Pick a labor category…"
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: selectableEntries.filter((e)=>e.id !== detail?.id).map((e)=>/*#__PURE__*/ _jsxs(SelectItem, {
                                                        value: e.id,
                                                        children: [
                                                            e.title,
                                                            e.companyName ? ` — ${e.companyName}` : ''
                                                        ]
                                                    }, e.id))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        disabled: (mapTargetKind === 'ANCHOR' ? !mapAnchorId : !mapEntryId) || propose.isPending || !detail || !!duplicateMapping,
                                        onClick: ()=>{
                                            if (!detail) return;
                                            propose.mutate({
                                                companyId: tenantId,
                                                fromEntryId: detail.id,
                                                toAnchorId: mapTargetKind === 'ANCHOR' ? mapAnchorId : undefined,
                                                toEntryId: mapTargetKind === 'ENTRY' ? mapEntryId : undefined,
                                                mappingType: mapType,
                                                evidenceJson: JSON.stringify(mapEvidence.trim() ? {
                                                    rationale: mapEvidence.trim()
                                                } : {})
                                            }, {
                                                onSuccess: (response)=>{
                                                    setProposalResult(!response.created && response.mapping?.status === 'REJECTED' ? 'Unchanged evidence matches an earlier rejection. It remains rejected; change target, relation or evidence to reconsider.' : response.created ? 'New pending proposal created. Earlier decisions remain in history.' : `Existing ${response.mapping?.status.toLowerCase() ?? ''} mapping retained.`);
                                                    setMapAnchorId('');
                                                    setMapEntryId('');
                                                }
                                            });
                                        },
                                        "data-testid": Testids.deliveryRegistries.lcatMapProposeButton,
                                        children: "Propose"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.deliveryRegistries.lcatRatesSection,
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                children: "Rates"
                            }),
                            detail && /*#__PURE__*/ _jsx(Link, {
                                to: rateBookHref({
                                    lcat: detail.id
                                }),
                                className: "inline-block text-xs text-primary underline-offset-2 hover:underline",
                                "data-testid": Testids.deliveryRegistries.rateBookOpen,
                                children: "Open in Rate Book"
                            }),
                            detail && /*#__PURE__*/ _jsx(EntryProvenanceSection, {
                                entry: detail,
                                candidates: acceptedCandidates
                            }),
                            detail && /*#__PURE__*/ _jsx(RateObservationHistory, {
                                companyId: tenantId,
                                rates: drawerRates,
                                canEdit: canEdit && !detailRetired,
                                entries: selectableEntries,
                                companies: companiesQuery.data?.items ?? [],
                                vehicles: vehiclesQuery.data?.items ?? []
                            }, detail.id),
                            drawerRatesQuery.isError && /*#__PURE__*/ _jsx("p", {
                                role: "alert",
                                children: "Unable to load rate history. Close and reopen to retry."
                            }),
                            drawerRates.length === 0 && !drawerRatesQuery.isLoading && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "No rates yet — record the posted ceiling and, when you learn it, what was actually agreed. The gap between those two is what the Rate Book is for."
                            }),
                            detail && !detailRetired && canEdit && /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: rateType,
                                        onValueChange: (v)=>{
                                            setRateType(v);
                                            if (rateUnit === 'FIXED' && PER_HOUR_TYPES.has(v)) setRateUnit(UNIT_NOT_STATED);
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 w-40 text-xs",
                                                "aria-label": "Rate type",
                                                "data-testid": Testids.deliveryRegistries.lcatRateTypeSelect,
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: Object.entries(ALL_RATE_TYPE_LABELS).map(([v, label])=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: v,
                                                        children: label
                                                    }, v))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        className: "h-9 w-24 text-xs",
                                        "aria-label": "Amount",
                                        placeholder: "185.00",
                                        value: rateAmount,
                                        "data-testid": Testids.deliveryRegistries.lcatRateAmountInput,
                                        onChange: (e)=>setRateAmount(e.target.value)
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraInput, {
                                        className: "h-9 flex-1 min-w-32 text-xs",
                                        "aria-label": "Period label",
                                        placeholder: "Period as written (e.g. 2025/2026)",
                                        value: ratePeriod,
                                        "data-testid": Testids.deliveryRegistries.lcatRatePeriodInput,
                                        onChange: (e)=>setRatePeriod(e.target.value)
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: rateBurden,
                                        onValueChange: setRateBurden,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 w-40 text-xs",
                                                "aria-label": "Burden basis",
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: Object.entries(BURDEN_LABELS).map(([v, label])=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: v,
                                                        children: label
                                                    }, v))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(Select, {
                                        value: rateUnit,
                                        onValueChange: setRateUnit,
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectTrigger, {
                                                className: "h-9 w-32 text-xs",
                                                "aria-label": "Unit",
                                                children: /*#__PURE__*/ _jsx(SelectValue, {})
                                            }),
                                            /*#__PURE__*/ _jsx(SelectContent, {
                                                children: Object.entries(RATE_UNIT_LABELS).filter(([v])=>!(v === 'FIXED' && PER_HOUR_TYPES.has(rateType))).map(([v, label])=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: v,
                                                        children: label
                                                    }, v))
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        disabled: !rateAmount || createRate.isPending,
                                        "data-testid": Testids.deliveryRegistries.lcatRateAdd,
                                        onClick: ()=>{
                                            if (!detail) return;
                                            const amount = Number(rateAmount);
                                            if (Number.isNaN(amount)) return;
                                            createRate.mutate({
                                                companyId: tenantId,
                                                lcatEntryId: detail.id,
                                                observationType: rateType,
                                                amount,
                                                burdenBasis: rateBurden,
                                                // "Not stated" sends nothing, so the backend stamps the
                                                // unit as an assumption instead of an asserted fact.
                                                rateUnit: rateUnit === UNIT_NOT_STATED ? '' : rateUnit,
                                                // The period is stored EXACTLY as typed; the comparison
                                                // layer is derived server-side, never in its place.
                                                periodLabel: ratePeriod,
                                                companyProfileId: detail.companyProfileId,
                                                contractVehicleId: detail.contractVehicleId
                                            }, {
                                                onSuccess: ()=>{
                                                    setRateAmount('');
                                                    setRatePeriod('');
                                                    setRateUnit(UNIT_NOT_STATED);
                                                }
                                            });
                                        },
                                        children: "Record rate"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.deliveryRegistries.lcatHistorySection,
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                children: "History"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "Saving an edit creates a new version below — it never overwrites the source text of the version being edited."
                            }),
                            history.map((v, i)=>{
                                // newest-first chain: the row one index further along is the
                                // version this one actually followed.
                                const previous = history[i + 1];
                                const changed = changedFields(v, previous);
                                // Rates pin the exact version they were observed against (see the
                                // Rates section above, which asks across_versions for this same
                                // reason) — counting per-version here is the proof that an old
                                // rate didn't float onto this edit.
                                const versionRateCount = drawerRates.filter((r)=>r.lcatEntryId === v.id).length;
                                return /*#__PURE__*/ _jsxs("div", {
                                    className: "rounded-md border border-border/60 p-2 text-[12.5px]",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsxs(SpecraBadge, {
                                                    variant: v.id === detail?.id ? 'dark' : 'neutral',
                                                    children: [
                                                        "v",
                                                        v.version
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "text-muted-foreground",
                                                    children: [
                                                        v.createdAt ? v.createdAt.toDate().toLocaleDateString() : '',
                                                        v.id === detail?.id ? ' · current' : ''
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "mt-1 font-medium",
                                            children: v.title
                                        }),
                                        v.qualifications && /*#__PURE__*/ _jsx("div", {
                                            className: "text-muted-foreground line-clamp-2",
                                            children: v.qualifications
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-muted-foreground",
                                            children: previous ? changed.length > 0 ? `Changed from v${previous.version}: ${changed.join(', ')}` : `No tracked fields changed from v${previous.version}` : 'First recorded version'
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "text-muted-foreground",
                                            children: [
                                                versionRateCount,
                                                " rate",
                                                versionRateCount === 1 ? '' : 's',
                                                " recorded against this exact version"
                                            ]
                                        })
                                    ]
                                }, v.id);
                            }),
                            history.length === 0 && !historyQuery.isLoading && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "No history yet."
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!retiring,
                onOpenChange: (open)=>!open && setRetiring(null),
                title: "Retire Labor Category",
                confirmLabel: "Retire",
                pendingLabel: "Retiring…",
                description: retiring ? `Retire "${retiring.title}" (v${retiring.version})? Its history and every rate or mapping already recorded against it are kept, and rates pinned to a version keep resolving. Only NEW selection is blocked: it can no longer be revised, get new rates, or be mapped to. Retired categories stay visible under "Show retired".` : undefined,
                onConfirm: async ()=>{
                    if (!retiring) return;
                    await retire.mutateAsync({
                        id: retiring.id,
                        companyId: tenantId
                    });
                    setRetiring(null);
                },
                isPending: retire.isPending
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Labor Category",
                // C2-11: say what deletion ACTUALLY does — it soft-deletes this entry,
                // and both the Rate Book and Mappings views join on live entries, so
                // its rates and mappings stop appearing from this point on. Retire
                // (RetireLaborCategoryEntry) is the keep-history path, offered on the row.
                description: deleting ? `Delete "${deleting.title}" (v${deleting.version})? This removes it from the catalog — its recorded rates and mappings stop appearing in the Rate Book and Mappings views. Use delete only for a record that should never have existed (erroneous or duplicate), not for a category that is simply no longer offered — use Retire for that, which keeps its history and rates.` : undefined,
                onConfirm: async ()=>{
                    if (!deleting) return;
                    await remove.mutateAsync(deleting.id);
                    setDeleting(null);
                },
                isPending: remove.isPending
            })
        ]
    });
}
